package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.data.dto.PersonDto
import com.arena_web.data.dto.ProfileCardDto
import com.arena_web.data.dto.UserDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.events.PopupOverlayFadeIn
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.edit.GhostPillButton
import com.arena_web.ui.sections.profile.edit.PrimaryOrGhostPillButton
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

// Figma 8415:117876 — "It's MATCH" popup. The full decorative chrome (dark
// surface, diagonal shards, halftone strips, "It's MATCH" wordmark, subtitle)
// is shipped as a single 674×475 SVG; the popup just needs to layer the two
// overlapping player cards above the artwork and pin the CTAs at the bottom.
private const val POPUP_BG_URL = "/images/match-popup-bg.svg"
private const val POPUP_WIDTH = 674
private const val POPUP_HEIGHT = 475
private const val POPUP_RADIUS = 26
// The two cards are PlayerCardSize.Medium (130×202). Per Figma 8415:117877 the
// second card overlaps onto the first by ~32px, so the row container is sized
// to (card + card − overlap) and each card is absolutely positioned inside.
private const val CARD_WIDTH = 130
private const val CARD_HEIGHT = 202
private const val CARD_OVERLAP = 32
private const val CARDS_ROW_WIDTH = CARD_WIDTH * 2 - CARD_OVERLAP

/**
 * "It's Match" popup — opens after a swipe on the People page resolves into a
 * mutual LIKE. Background artwork is the single SVG above; only the two
 * overlapping [PlayerCard]s (top) and the action pills (bottom) are dynamic.
 *   • Keep Swiping  — dismisses, control returns to the card stack.
 *   • Go to Profile — navigates to the matched user's public profile.
 */
@Composable
fun MatchPopup(
    matched: PersonDto?,
    currentUser: UserDto?,
    currentUserCard: ProfileCardDto?,
    sportName: String,
    onClose: () -> Unit,
    onGoToProfile: (userId: String) -> Unit,
) {
    if (matched == null) return

    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1100)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { onClose() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        // Aspect-locked surface — the SVG is 674×475 and bakes in the corner
        // radius, so we mirror those dimensions and let the artwork drive the
        // visual frame (no border, no internal CSS chrome).
        Box(
            modifier = Modifier
                .sMaxWidth(POPUP_WIDTH)
                .fillMaxWidth()
                .sMargin(16)
                .sBorderRadius(POPUP_RADIUS)
                .onClick { it.stopPropagation() }
                .styleModifier {
                    property("position", "relative")
                    property("aspect-ratio", "$POPUP_WIDTH / $POPUP_HEIGHT")
                    property("box-sizing", "border-box")
                    property("overflow", "hidden")
                    property("background-image", "url('$POPUP_BG_URL')")
                    property("background-repeat", "no-repeat")
                    property("background-size", "100% 100%")
                    property("background-position", "center")
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                }
                .animation(
                    ModalFadeInKeyframes.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
        ) {
            // Cards float at the top, buttons stay flush against the bottom;
            // the flex spacer in between lets the SVG art (cluster of dots +
            // "It's MATCH" wordmark) read through cleanly.
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("padding", "${s(40)} ${s(22)} ${s(28)} ${s(22)}")
                        property("position", "relative")
                        property("z-index", "1")
                    },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                MatchCardsRow(
                    matched = matched,
                    currentUser = currentUser,
                    currentUserCard = currentUserCard,
                    sportName = sportName,
                )
                Box(modifier = Modifier.flexGrow(1))
                MatchButtons(
                    onKeepSwiping = onClose,
                    onGoToProfile = { onGoToProfile(matched.userId) },
                )
            }
        }
    }
}

@Composable
private fun MatchCardsRow(
    matched: PersonDto,
    currentUser: UserDto?,
    currentUserCard: ProfileCardDto?,
    sportName: String,
) {
    // Outer wrapper centres the fixed-width card frame horizontally inside
    // the popup. Absolute positioning is the only reliable way to guarantee
    // the cards overlap by exactly CARD_OVERLAP — flex + negative margin
    // sometimes computes wrong widths once the popup itself reflows.
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center
    ) {
        Box(
            modifier = Modifier
                .styleModifier {
                    property("position", "relative")
                    property("width", s(CARDS_ROW_WIDTH))
                    property("height", s(CARD_HEIGHT))
                    property("flex-shrink", "0")
                }
        ) {
            // Left card — caller's main profile card, anchored to the left
            // edge of the row.
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("left", "0")
                        property("top", "0")
                        property("width", s(CARD_WIDTH))
                        property("height", s(CARD_HEIGHT))
                        property("z-index", "1")
                    }
            ) {
                PlayerCard(
                    playerName = displayName(
                        fullName = currentUser?.fullName,
                        fallback = currentUser?.username ?: LocalAppStrings.current.matchYouFallback,
                        dateOfBirthIso = currentUser?.dateOfBirth,
                    ),
                    rating = 0,
                    position = "",
                    sport = sportName,
                    size = PlayerCardSize.Medium,
                    avatarUrl = currentUserCard?.avatarUrl,
                    hideRating = true,
                    hideShadow = true,
                    cardAssetsBasePath = currentUserCard?.assetsBasePath,
                    cardTextColor = currentUserCard?.textColor,
                )
            }

            // Right card — matched person, anchored to the right edge so the
            // two cards meet in the middle with exactly CARD_OVERLAP of
            // overlap. Higher z-index so it visibly sits on top of the left
            // card per Figma 8415:117877.
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("position", "absolute")
                        property("right", "0")
                        property("top", "0")
                        property("width", s(CARD_WIDTH))
                        property("height", s(CARD_HEIGHT))
                        property("z-index", "2")
                    }
            ) {
                PlayerCard(
                    playerName = displayNameWithAge(
                        fullName = matched.fullName,
                        fallback = matched.username,
                        age = matched.age,
                    ),
                    rating = 0,
                    position = "",
                    sport = matched.cardTemplateName ?: sportName,
                    size = PlayerCardSize.Medium,
                    avatarUrl = matched.avatarUrl,
                    hideRating = true,
                    hideShadow = true,
                    cardAssetsBasePath = matched.cardAssetsBasePath,
                    cardTextColor = matched.cardTextColor,
                )
            }
        }
    }
}

@Composable
private fun MatchButtons(onKeepSwiping: () -> Unit, onGoToProfile: () -> Unit) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.fillMaxWidth().sGap(12),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        GhostPillButton(label = t.matchKeepSwiping, onClick = onKeepSwiping)
        PrimaryOrGhostPillButton(label = t.matchGoToProfile, primary = true, onClick = onGoToProfile)
    }
}

// "JAMES, 26" — uppercase first name + age. Age is derived from the ISO
// date-of-birth on the user row; falls back to just the first name when DOB is
// missing (rare — new accounts before personal-info save).
private fun displayName(fullName: String?, fallback: String, dateOfBirthIso: String?): String {
    val first = firstNameOf(fullName).ifBlank { fallback }
    val age = ageFromIso(dateOfBirthIso)
    return if (age != null) "${first.uppercase()}, $age" else first.uppercase()
}

private fun displayNameWithAge(fullName: String?, fallback: String, age: Int): String {
    val first = firstNameOf(fullName).ifBlank { fallback }
    return "${first.uppercase()}, $age"
}

private fun firstNameOf(fullName: String?): String =
    fullName.orEmpty().trim().substringBefore(' ').trim()

private fun ageFromIso(iso: String?): Int? {
    if (iso.isNullOrBlank()) return null
    val datePart = iso.substringBefore('T').trim()
    val parts = datePart.split('-')
    if (parts.size != 3) return null
    val (y, m, d) = parts.map { it.toIntOrNull() ?: return null }
    val today = kotlin.js.Date()
    val nowYear = today.getFullYear()
    val nowMonth = today.getMonth() + 1
    val nowDay = today.getDate()
    var years = nowYear - y
    val hadBirthday = nowMonth > m || (nowMonth == m && nowDay >= d)
    if (!hadBirthday) years -= 1
    return years.coerceAtLeast(0)
}
